import React from 'react';
import { useUOM } from '../../contexts/UOMContext';
import { useLanguage } from '../../contexts/LanguageContext';
import './UOMSelect.css';

/**
 * UOMSelect - Reusable Unit of Measure dropdown component
 *
 * Usage:
 *   <UOMSelect
 *     value={data.unit_of_measure}
 *     onChange={(value) => handleChange('unit_of_measure', value)}
 *     disabled={!editMode}
 *   />
 *
 * Props:
 *   - value: Current UOM code (e.g., 'KG', 'PCS')
 *   - onChange: Callback function receiving the new UOM code
 *   - disabled: Whether the select is disabled
 *   - grouped: Show options grouped by category (default: true)
 *   - filterCategories: Array of categories to show (e.g., ['WEIGHT', 'QUANTITY'])
 *   - className: Additional CSS classes
 *   - placeholder: Placeholder text when no value selected
 *   - required: Whether field is required
 *   - name: Form field name
 *   - id: Element ID
 */
function UOMSelect({
  value,
  onChange,
  disabled = false,
  grouped = true,
  filterCategories = null,
  className = '',
  placeholder = null,
  required = false,
  name = 'unit_of_measure',
  id = null
}) {
  const { uoms, uomsByCategory, loading } = useUOM();
  const { currentLanguage } = useLanguage();

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  // Get the label for a UOM based on current language
  const getDisplayLabel = (uom) => {
    if (currentLanguage === 'es' && uom.uom_name_es) {
      return `${uom.uom_name_es} (${uom.uom_code})`;
    }
    return `${uom.uom_name} (${uom.uom_code})`;
  };

  // Category labels for grouping
  const categoryLabels = {
    QUANTITY: currentLanguage === 'es' ? 'Cantidad' : 'Quantity',
    WEIGHT: currentLanguage === 'es' ? 'Peso' : 'Weight',
    LENGTH: currentLanguage === 'es' ? 'Longitud' : 'Length',
    AREA: currentLanguage === 'es' ? 'Area' : 'Area',
    VOLUME: currentLanguage === 'es' ? 'Volumen' : 'Volume'
  };

  // Filter categories if specified
  const categories = filterCategories || ['QUANTITY', 'WEIGHT', 'LENGTH', 'AREA', 'VOLUME'];

  // Render grouped options
  const renderGroupedOptions = () => {
    return categories.map(category => {
      const categoryUoms = uomsByCategory[category] || [];
      if (categoryUoms.length === 0) return null;

      return (
        <optgroup key={category} label={categoryLabels[category]}>
          {categoryUoms.map(uom => (
            <option key={uom.uom_code} value={uom.uom_code}>
              {getDisplayLabel(uom)}
            </option>
          ))}
        </optgroup>
      );
    });
  };

  // Render flat options (not grouped)
  const renderFlatOptions = () => {
    let filteredUoms = uoms;
    if (filterCategories) {
      filteredUoms = uoms.filter(uom => filterCategories.includes(uom.uom_category));
    }

    return filteredUoms.map(uom => (
      <option key={uom.uom_code} value={uom.uom_code}>
        {getDisplayLabel(uom)}
      </option>
    ));
  };

  // Default placeholder based on language
  const defaultPlaceholder = currentLanguage === 'es'
    ? 'Seleccionar unidad...'
    : 'Select unit...';

  return (
    <select
      id={id || `uom-select-${name}`}
      name={name}
      value={value || ''}
      onChange={handleChange}
      disabled={disabled || loading}
      required={required}
      className={`uom-select ${className} ${loading ? 'loading' : ''}`}
    >
      <option value="" disabled hidden>{placeholder || defaultPlaceholder}</option>
      {grouped ? renderGroupedOptions() : renderFlatOptions()}
    </select>
  );
}

export default UOMSelect;
